
import { useCallback, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useCacheCoordinator } from "./useCacheCoordinator";
import { toast } from "@/hooks/use-toast";

interface UseUnifiedOptimizedReorderProps {
  routineId: string;
  exercises: any[];
  onLocalUpdate: (exercises: any[]) => void;
  isPersonalPlan?: boolean;
}

interface ReorderOperation {
  id: string;
  sourceIndex: number;
  destinationIndex: number;
  timestamp: number;
  status: 'pending' | 'syncing' | 'completed' | 'failed';
}

export const useUnifiedOptimizedReorder = ({
  routineId,
  exercises,
  onLocalUpdate,
  isPersonalPlan = false
}: UseUnifiedOptimizedReorderProps) => {
  const { 
    lockOperation, 
    unlockOperation, 
    isOperationLocked,
    isDragReady,
    updateExerciseCache
  } = useCacheCoordinator({ routineId });

  const [pendingOperations, setPendingOperations] = useState<Map<string, ReorderOperation>>(new Map());
  const backgroundTasksRef = useRef<Map<string, Promise<void>>>(new Map());
  const operationSequenceRef = useRef<number>(0);

  // Sistema de validación de integridad
  const validateExerciseOrder = useCallback((exerciseList: any[]) => {
    const positions = exerciseList.map(ex => ex.position).sort((a, b) => a - b);
    const expectedPositions = Array.from({ length: exerciseList.length }, (_, i) => i + 1);
    
    return JSON.stringify(positions) === JSON.stringify(expectedPositions);
  }, []);

  // Función de actualización optimística ultra-rápida con validación
  const applyOptimisticUpdate = useCallback((
    sourceIndex: number,
    destinationIndex: number
  ) => {
    const operationId = `reorder-${isPersonalPlan ? 'personal' : 'standard'}-${++operationSequenceRef.current}`;
    
    console.log(`⚡ Ultra-optimistic update [${operationId}]:`, {
      sourceIndex,
      destinationIndex,
      isPersonalPlan,
      exerciseCount: exercises.length
    });

    // Crear nueva lista reordenada
    const reorderedExercises = Array.from(exercises);
    const [movedExercise] = reorderedExercises.splice(sourceIndex, 1);
    reorderedExercises.splice(destinationIndex, 0, movedExercise);

    // Actualizar posiciones de forma eficiente
    const exercisesWithNewPositions = reorderedExercises.map((exercise, index) => ({
      ...exercise,
      position: index + 1
    }));

    // Validar integridad antes de aplicar
    if (!validateExerciseOrder(exercisesWithNewPositions)) {
      console.error(`❌ Order validation failed for operation ${operationId}`);
      throw new Error('Invalid exercise order detected');
    }

    // Aplicar actualizaciones instantáneas
    updateExerciseCache(exercisesWithNewPositions);
    onLocalUpdate(exercisesWithNewPositions);

    // Registrar operación pendiente
    setPendingOperations(prev => new Map(prev).set(operationId, {
      id: operationId,
      sourceIndex,
      destinationIndex,
      timestamp: Date.now(),
      status: 'pending'
    }));

    console.log(`✅ Optimistic update applied [${operationId}]`);
    return { operationId, exercisesWithNewPositions };
  }, [exercises, updateExerciseCache, onLocalUpdate, isPersonalPlan, validateExerciseOrder]);

  // Tarea en segundo plano ultra-optimizada
  const executeBackgroundUpdate = useCallback(async (
    exercisesWithNewPositions: any[],
    operationId: string
  ) => {
    try {
      // Lock mínimo solo para la operación de base de datos
      lockOperation(operationId);

      // Actualizar estado de operación
      setPendingOperations(prev => {
        const newMap = new Map(prev);
        const operation = newMap.get(operationId);
        if (operation) {
          newMap.set(operationId, { ...operation, status: 'syncing' });
        }
        return newMap;
      });

      // Preparar datos para actualización batch
      const updateData = exercisesWithNewPositions.map((exercise, index) => ({
        id: exercise.id,
        position: index + 1
      }));

      console.log(`🚀 Background ultra-sync [${operationId}]:`, {
        isPersonalPlan,
        updateCount: updateData.length,
        routineId
      });

      // Usar función batch optimizada con timeout
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('DB_TIMEOUT')), isPersonalPlan ? 5000 : 4000)
      );

      const updatePromise = supabase.rpc('update_exercise_positions_batch', {
        p_routine_id: routineId,
        p_updates: updateData
      });

      const { error } = await Promise.race([updatePromise, timeoutPromise]) as any;

      if (error) {
        console.error(`❌ Background update failed [${operationId}]:`, error);
        throw error;
      }

      // Operación completada exitosamente
      setPendingOperations(prev => {
        const newMap = new Map(prev);
        const operation = newMap.get(operationId);
        if (operation) {
          newMap.set(operationId, { ...operation, status: 'completed' });
        }
        return newMap;
      });

      console.log(`✅ Background ultra-sync completed [${operationId}]`);

    } catch (error) {
      console.error(`❌ Background update error [${operationId}]:`, error);
      
      // Marcar operación como fallida
      setPendingOperations(prev => {
        const newMap = new Map(prev);
        const operation = newMap.get(operationId);
        if (operation) {
          newMap.set(operationId, { ...operation, status: 'failed' });
        }
        return newMap;
      });

      const isTimeout = error?.message?.includes('DB_TIMEOUT');
      
      if (!isTimeout) {
        // Solo revertir si no es timeout (el timeout puede ser temporal)
        console.log(`🔄 Reverting optimistic update [${operationId}]`);
        updateExerciseCache(exercises);
        onLocalUpdate(exercises);
        
        toast({
          variant: "destructive",
          title: "Error de sincronización",
          description: "Los cambios se revirtieron debido a un error en el servidor."
        });
      } else {
        console.log(`⏰ Database timeout [${operationId}] - keeping optimistic update`);
      }
      
      throw error;
    } finally {
      unlockOperation(operationId);
      
      // Limpiar operación después de un delay
      setTimeout(() => {
        setPendingOperations(prev => {
          const newMap = new Map(prev);
          newMap.delete(operationId);
          return newMap;
        });
      }, 5000);
    }
  }, [routineId, isPersonalPlan, lockOperation, unlockOperation, updateExerciseCache, onLocalUpdate, exercises]);

  // Función principal de reordenamiento ultra-optimizada
  const reorderExercises = useCallback(async (
    sourceIndex: number,
    destinationIndex: number
  ) => {
    if (!exercises?.length || sourceIndex === destinationIndex) return;

    try {
      // 1. Aplicar actualización optimística instantánea
      const { operationId, exercisesWithNewPositions } = applyOptimisticUpdate(sourceIndex, destinationIndex);
      
      // 2. Configurar tarea en segundo plano
      const backgroundTask = executeBackgroundUpdate(exercisesWithNewPositions, operationId);
      
      // Almacenar referencia de la tarea
      backgroundTasksRef.current.set(operationId, backgroundTask);

      // 3. Fire-and-forget - no bloquear la UI
      backgroundTask
        .then(() => {
          console.log(`✅ Background task completed [${operationId}]`);
        })
        .catch(error => {
          console.error(`❌ Background task failed [${operationId}]:`, error);
        })
        .finally(() => {
          backgroundTasksRef.current.delete(operationId);
        });

      return operationId;

    } catch (error) {
      console.error("❌ Error in ultra-optimized reorder:", error);
      throw error;
    }
  }, [exercises, applyOptimisticUpdate, executeBackgroundUpdate]);

  // Función para verificar si se puede reordenar
  const canReorder = useCallback(() => {
    return exercises?.length > 0 && isDragReady() && pendingOperations.size < 5; // Límite de operaciones concurrentes
  }, [exercises, isDragReady, pendingOperations]);

  // Función para obtener estadísticas de operaciones
  const getOperationStats = useCallback(() => {
    const operations = Array.from(pendingOperations.values());
    return {
      total: operations.length,
      pending: operations.filter(op => op.status === 'pending').length,
      syncing: operations.filter(op => op.status === 'syncing').length,
      completed: operations.filter(op => op.status === 'completed').length,
      failed: operations.filter(op => op.status === 'failed').length,
      backgroundTasks: backgroundTasksRef.current.size
    };
  }, [pendingOperations]);

  // Función para esperar tareas pendientes
  const waitForPendingOperations = useCallback(async () => {
    const pendingTasks = Array.from(backgroundTasksRef.current.values());
    if (pendingTasks.length > 0) {
      console.log(`⏳ Waiting for ${pendingTasks.length} background tasks`);
      await Promise.allSettled(pendingTasks);
    }
  }, []);

  // Función para cancelar operaciones pendientes
  const cancelPendingOperations = useCallback(() => {
    backgroundTasksRef.current.clear();
    setPendingOperations(new Map());
    console.log("🚫 All pending operations cancelled");
  }, []);

  // Función para reintentar operaciones fallidas
  const retryFailedOperations = useCallback(async () => {
    const failedOperations = Array.from(pendingOperations.values())
      .filter(op => op.status === 'failed');
    
    if (failedOperations.length === 0) return;

    console.log(`🔄 Retrying ${failedOperations.length} failed operations`);
    
    for (const operation of failedOperations) {
      try {
        await reorderExercises(operation.sourceIndex, operation.destinationIndex);
      } catch (error) {
        console.error(`❌ Retry failed for operation ${operation.id}:`, error);
      }
    }
  }, [pendingOperations, reorderExercises]);

  return {
    reorderExercises,
    canReorder,
    hasPendingOperations: pendingOperations.size > 0,
    pendingOperationsCount: pendingOperations.size,
    getOperationStats,
    waitForPendingOperations,
    cancelPendingOperations,
    retryFailedOperations,
    isOperationLocked: isOperationLocked(),
    
    // Estadísticas detalladas para debugging
    getPendingOperations: () => Array.from(pendingOperations.values()),
    getBackgroundTasksCount: () => backgroundTasksRef.current.size
  };
};
